import { useState } from 'react';
import { Alert, AlertTitle, Button, CircularProgress, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import api from 'services/api';
import { useAlert } from 'contexts/alert';
import { Modal } from 'v2/components';

import { IBeneficiary, IActivity } from './TemplateMovementsCheck.types';
import { contingencyAge } from './TemplateMovementsCheck.utils';

type Outcome = 'APPLIED' | 'REQUEUE';

const DIALOG_COPY: Record<Outcome, { title: string; body: string; confirmLabel: string }> = {
  APPLIED: {
    title: 'Marcar como aplicado manualmente?',
    body:
      'Confirme que a corretora concluiu a carga na operadora. O lote será encerrado '
      + 'como entregue e não voltará a ser exportado.',
    confirmLabel: 'Confirmar conclusão',
  },
  REQUEUE: {
    title: 'Devolver ao fluxo da plataforma?',
    body:
      'O lote voltará ao caminho automático e será reenviado na próxima exportação. '
      + 'Use apenas se NADA foi aplicado manualmente — caso contrário as movimentações '
      + 'serão duplicadas na operadora.',
    confirmLabel: 'Devolver ao fluxo',
  },
};

/**
 * Banner de lote aguardando conclusão manual na operadora.
 *
 * Aparece quando a massa do ciclo tem linhas em CONTINGENCY. A `observation`
 * vem do backend e já diz qual é o curso (carga manual, conclusão no portal da
 * QBem, ou verificação), então o texto não é reconstruído aqui.
 */
export function ContingencyBanner({
  people,
  activities,
}: {
  people: IBeneficiary[];
  activities: IActivity[];
}) {
  const { workspaceId, templateId } = useParams() as { workspaceId: string; templateId: string };
  const { openAlert } = useAlert();
  const queryClient = useQueryClient();

  const [pendingOutcome, setPendingOutcome] = useState<Outcome | null>(null);
  const [isResolving, setIsResolving] = useState(false);
  // A tela só vê a geração mais recente; linhas concluídas em geração anterior do
  // mesmo lote (FSFX: 31 em 04/10) só o backend enxerga. Recusado o REQUEUE, a
  // tela passa a tratar o lote como parcialmente concluído.
  // ponytail: só aprende depois do 1º clique; campo no movements-check se incomodar.
  const [requeueRecusado, setRequeueRecusado] = useState(false);

  const inContingency = people.filter((p) => p.status === 'CONTINGENCY');
  if (inContingency.length === 0) return null;

  // Todas as linhas do lote carregam a mesma observação, gravada em bloco pelo
  // markContingency. A primeira não-vazia serve de motivo para o banner inteiro.
  const observation = inContingency.find((p) => !!p.observation)?.observation ?? null;

  // D6 (spec 2026-10-13): a gravidade cresce com o tempo aberto, não com o número de pessoas.
  const idade = contingencyAge(inContingency.map((p) => p.observation));

  // D7: com qualquer pessoa já concluída fora da plataforma, o lote foi entregue em
  // parte; devolver o resto ao fluxo duplicaria movimentação (o backend também recusa).
  const loteParcialmenteConcluido =
    requeueRecusado || people.some((p) => p.status === 'CONTINGENCY_APPLIED');

  async function handleResolve(outcome: Outcome) {
    try {
      setIsResolving(true);

      // Qualquer atividade do workspace serve: o backend resolve o workspace a
      // partir dela. Usamos a de aprovação por ser a que esta tela já garante.
      const activityId =
        activities.find((a) => a.slug === 'app_approve')?.workspaceActivityId
        ?? activities[0]?.workspaceActivityId;

      if (!activityId) {
        throw new Error('Identificador da atividade não encontrado nesta tela.');
      }

      await api.post(`/v2/apps/contingency/${activityId}/resolve`, { outcome });

      // A massa aparece em três chaves distintas. Invalidar só a desta tela
      // deixaria a tela de exportação servindo dados velhos.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['movements-check', workspaceId, templateId] }),
        queryClient.invalidateQueries({ queryKey: ['movements-export', workspaceId, templateId] }),
        queryClient.invalidateQueries({ queryKey: ['movimentacao-atividade', workspaceId, templateId] }),
      ]);

      openAlert(
        outcome === 'APPLIED'
          ? 'Lote encerrado como aplicado manualmente.'
          : 'Lote devolvido ao fluxo da plataforma.',
        'success',
      );
      setPendingOutcome(null);
    } catch (error: any) {
      // A mensagem do backend é acionável ("resolva na tela de movimentações
      // antes de exportar", "pode já ter sido resolvida por outro usuário") e
      // precisa chegar ao operador em vez de virar erro genérico.
      const backendMessage = error?.response?.data?.data?.message;
      if (outcome === 'REQUEUE' && error?.response?.status === 400) {
        setRequeueRecusado(true);
        setPendingOutcome(null);
      }
      openAlert(
        backendMessage || 'Erro ao resolver a contingência do lote.',
        'error',
      );
    } finally {
      setIsResolving(false);
    }
  }

  const dialog = pendingOutcome ? DIALOG_COPY[pendingOutcome] : null;

  return (
    <>
      <Alert severity={idade.severity} style={{ marginBottom: '1rem' }}>
        <AlertTitle>
          Lote aguardando conclusão manual
          {idade.dias !== null && `, aberto há ${idade.dias} ${idade.dias === 1 ? 'dia' : 'dias'}`}
        </AlertTitle>
        <Typography variant="body2" style={{ marginBottom: '0.5rem' }}>
          {inContingency.length} registro(s) deste ciclo saíram do envio automático e
          aguardam conclusão manual na operadora. Enquanto a pendência não for resolvida,
          este lote não será reexportado.
        </Typography>
        {observation && (
          <Typography variant="body2" style={{ marginBottom: '0.75rem', fontStyle: 'italic' }}>
            {observation}
          </Typography>
        )}
        {loteParcialmenteConcluido && (
          <Typography variant="body2" style={{ marginBottom: '0.75rem' }}>
            Parte deste lote já foi concluída fora da plataforma. Por isso ele não pode voltar
            ao fluxo: use &quot;Marcar como aplicado manualmente&quot; para o que falta.
          </Typography>
        )}
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <Button
            variant="contained"
            size="small"
            disabled={isResolving}
            onClick={() => setPendingOutcome('APPLIED')}
          >
            Marcar como aplicado manualmente
          </Button>
          {!loteParcialmenteConcluido && (
            <Button
              variant="outlined"
              size="small"
              color="error"
              disabled={isResolving}
              onClick={() => setPendingOutcome('REQUEUE')}
            >
              Devolver ao fluxo da plataforma
            </Button>
          )}
        </div>
      </Alert>

      {dialog && (
        <Modal open size="md">
          <Typography variant="h6" style={{ marginBottom: '1rem' }}>{dialog.title}</Typography>
          <Typography variant="body2" style={{ marginBottom: '1.5rem' }}>{dialog.body}</Typography>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
            <Button
              variant="outlined"
              disabled={isResolving}
              onClick={() => setPendingOutcome(null)}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              color={pendingOutcome === 'REQUEUE' ? 'error' : 'primary'}
              disabled={isResolving}
              onClick={() => handleResolve(pendingOutcome!)}
            >
              {isResolving ? <CircularProgress size={20} /> : dialog.confirmLabel}
            </Button>
          </div>
        </Modal>
      )}
    </>
  );
}
